import type { StateCreator } from "zustand";
import type { DBOpsState } from "../useStudioAPI/hooks/dbOps.store";
import type { Change, LedgerTxMetaData, MegaJSON } from "@certa/studio-core";
import { initState } from "@certa/studio-core/src/state/stateManager";
import { getMegaJSONFromPersistentStorage } from "../dbOps.persistentStorage.utils";
import { devLogger } from "@certa/common/utils/devLogger";
import { set as lodashSet, unset as lodashUnset } from "lodash-es";
import { nanoid } from "nanoid";
const UNDO_REDO_CONTEXTS = ["canvas", "eca"] as const;

export type TransactionMetaData = {
  source?: string;
  skipLoggingInLedgerAndUndoStack?: boolean;
} & LedgerTxMetaData;

export type StateManagerSlice = {
  stateManager:
    | ReturnType<typeof initState<MegaJSON, typeof UNDO_REDO_CONTEXTS>>
    | undefined;

  initialize: ({
    savepointId,
    isECAEnabled,
    isFlowModeEnabled,
    isReadOnly
  }: {
    savepointId: string;
    isECAEnabled?: boolean;
    isFlowModeEnabled?: boolean;
    isReadOnly: boolean;
  }) => Promise<void>;
  deConstructor: () => void;

  isEditModeEnabled: boolean;
  setIsEditModeEnabled: (isEditModeEnabled: boolean) => void;

  throwErrorIfNotInEditMode: () => void;

  applyChanges: (changes: Change[]) => void;
  undo: () => void;
  redo: () => void;
  clearUndoStack: (processId: string) => void;
  clearRedoStack: (processId: string) => void;
  revertToParticularCommit: (commitId: string | null) => void;
  clearChangesForApp: () => void;

  // Transaction
  isTransactionInProgress: boolean;
  changesGeneratedByActiveTransaction: Change[];
  transactionStack: string[]; // This will have more than one entries in case of nested transactions
  transactionMetaData: TransactionMetaData | undefined;

  transaction: <T>(callback: () => T, metaData?: TransactionMetaData) => T;
  endTransaction: () => void;
};

export const stateManagerSlice: StateCreator<
  DBOpsState,
  [],
  [],
  StateManagerSlice
> = (set, get) => ({
  stateManager: undefined,

  initialize: async ({
    savepointId,
    isECAEnabled,
    isFlowModeEnabled,
    isReadOnly
  }) => {
    const megaJSON = await getMegaJSONFromPersistentStorage(savepointId);
    if (!megaJSON) {
      devLogger.error(`megaJSON not found with savepointId: ${savepointId}`);
      return;
    }

    const stateManager = initState(
      { ...megaJSON, id: savepointId } as MegaJSON,
      UNDO_REDO_CONTEXTS,
      "canvas"
    );

    // Initialize worker and start periodic saves
    get().deConstructor();
    get().initializeMegaJSONWorker(isReadOnly);

    set({
      megaJSON: { ...megaJSON, id: savepointId },
      isECAEnabled,
      isFlowModeEnabled,
      stateManager
    });
  },

  deConstructor: () => {
    get().terminateMegaJSONWorker();
  },

  isEditModeEnabled: false,
  setIsEditModeEnabled: (isEditModeEnabled: boolean) => {
    set({ isEditModeEnabled });
  },
  throwErrorIfNotInEditMode: () => {
    if (
      !get().isEditModeEnabled &&
      !get().transactionMetaData?.skipLoggingInLedgerAndUndoStack
    ) {
      // This should ideally never occur, but throwing error to monitor this error on sentry
      throw new Error("dbOps: Process is not in edit mode");
    }
  },

  applyChanges: (changes: Change[]) => {
    get().throwErrorIfNotInEditMode();
    if (changes.length === 0) return;

    const { isTransactionInProgress, stateManager, transactionMetaData } =
      get();

    // If we're in a transaction, accumulate changes
    if (isTransactionInProgress) {
      set(state => ({
        changesGeneratedByActiveTransaction: [
          ...state.changesGeneratedByActiveTransaction,
          ...changes
        ]
      }));
      return;
    }

    // Otherwise, apply changes immediately
    set(state => {
      const newMegaJSON = { ...state.megaJSON };

      changes.forEach(change => {
        // Handle deletion
        if (change.to === undefined) {
          // Modifying draft state directly
          lodashUnset(newMegaJSON, change.path);
          return;
        }

        // Handle addition/update
        // Modifying draft state directly
        lodashSet(newMegaJSON, change.path, change.to);
      });

      return {
        megaJSON: newMegaJSON,
        changesGeneratedByActiveTransaction: []
      };
    });

    // Commit changes to state manager
    stateManager?.commit(changes, {
      shouldSkipLedger: transactionMetaData?.skipLoggingInLedgerAndUndoStack,
      shouldSkipUndoStack: transactionMetaData?.skipLoggingInLedgerAndUndoStack,
      transactionMetadata: transactionMetaData
    });
  },

  undo: () => {
    get().throwErrorIfNotInEditMode();
    const { stateManager, transactionMetaData } = get();
    stateManager?.undo(transactionMetaData);
    // Just to trigger a re-render

    set({ megaJSON: { ...stateManager?.state } as MegaJSON });
  },

  redo: () => {
    get().throwErrorIfNotInEditMode();
    const { stateManager, transactionMetaData } = get();
    stateManager?.redo(transactionMetaData);
    // Just to trigger a re-render
    set({ megaJSON: { ...stateManager?.state } as MegaJSON });
  },

  revertToParticularCommit: (commitId: string | null) => {
    get().throwErrorIfNotInEditMode();
    const { stateManager, transactionMetaData } = get();
    stateManager?.revertToParticularCommit(commitId, transactionMetaData);
    // Just to trigger a re-render
    set({ megaJSON: { ...stateManager?.state } as MegaJSON });
  },

  clearUndoStack: (processId: string) => {
    const { stateManager } = get();
    stateManager?.clearUndoStack();
  },

  clearRedoStack: (processId: string) => {
    const { stateManager } = get();
    stateManager?.clearRedoStack();
  },

  clearChangesForApp: () => {
    const { stateManager } = get();
    stateManager?.clearLedger();
  },

  isTransactionInProgress: false,
  changesGeneratedByActiveTransaction: [],
  transactionStack: [],
  transactionMetaData: undefined,
  transaction: <T extends any>(
    callback: () => T,
    metaData?: TransactionMetaData
  ): T => {
    try {
      set(state => ({
        isTransactionInProgress: true,
        transactionStack: [...state.transactionStack, nanoid()],
        transactionMetaData: {
          ...metaData,
          ...state.transactionMetaData
        }
      }));

      const result = callback();

      set(state => {
        const newStack = [...state.transactionStack];
        newStack.pop();

        if (newStack.length === 0) {
          get().endTransaction();
          get().applyChanges(state.changesGeneratedByActiveTransaction);
          return {
            transactionStack: newStack,
            transactionMetaData: undefined
          };
        }

        return { transactionStack: newStack };
      });

      return result;
    } catch (err) {
      devLogger.error("In memory JSON transaction failed: ", err);
      set({
        changesGeneratedByActiveTransaction: [],
        transactionMetaData: undefined,
        isTransactionInProgress: false,
        transactionStack: []
      });
      throw err;
    }
  },

  endTransaction: () => {
    set({
      isTransactionInProgress: false,
      transactionStack: []
    });
  }
});
